import React, { useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";

/**
 * Renders a download button for a completed data download job.
 * downloadURL is a JSON object mapping batteryID -> pre-signed S3 URL.
 * - Single file: clicking directly opens the download URL.
 * - Multiple files: clicking opens a dropdown listing each battery's file.
 * urlExpiresAt is an optional datetime string; if past expiry the button shows
 * a disabled "Expired" state as a fallback (backend should normally refresh URLs).
 */
const DownloadButton = ({ downloadURL, urlExpiresAt }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!downloadURL) return null;

  const isExpired = urlExpiresAt ? moment(urlExpiresAt).isBefore(moment()) : false;

  let urlMap = {};
  try {
    urlMap = typeof downloadURL === "string" ? JSON.parse(downloadURL) : downloadURL;
  } catch {
    return null;
  }

  const entries = Object.entries(urlMap);
  if (entries.length === 0) return null;

  const buttonSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: "6px",
    border: "1px solid #4D44D8",
    color: "#4D44D8",
    cursor: "pointer",
    bgcolor: "transparent",
    "&:hover": { bgcolor: "#F0EFFF" },
  };

  if (isExpired) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: "6px",
          border: "1px solid #BDBDBD",
          color: "#9E9E9E",
          bgcolor: "transparent",
          cursor: "not-allowed",
        }}
      >
        <DownloadIcon sx={{ fontSize: "16px" }} />
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#9E9E9E" }}>
          Expired
        </Typography>
      </Box>
    );
  }

  if (entries.length === 1) {
    const [, url] = entries[0];
    return (
      <Box
        component="button"
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        sx={buttonSx}
      >
        <DownloadIcon sx={{ fontSize: "16px" }} />
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#4D44D8" }}>
          Download
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={buttonSx}
      >
        <DownloadIcon sx={{ fontSize: "16px" }} />
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#4D44D8" }}>
          Download ({entries.length})
        </Typography>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {entries.map(([batteryId, url]) => (
          <MenuItem
            key={batteryId}
            onClick={() => {
              window.open(url, "_blank", "noopener,noreferrer");
              setAnchorEl(null);
            }}
            sx={{ fontSize: "13px", gap: 1 }}
          >
            <DownloadIcon sx={{ fontSize: "15px", color: "#4D44D8" }} />
            {batteryId}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default DownloadButton;
